import { useMemo } from "react";
import { servicesPartnersGroupId } from "@atlas-int/data";
import { getProductClassServicesCategoryId } from "@atlas-int/viz";
import { ProductLevel, TradeFlow, TradeDirection, TradeDirectionSelectorString } from "@atlas-int/data/types";
import { mergeDataWithTopLevelParent } from "../../../Utils";
import { worldGroupDatum, computeMonetaryValueByTradeFlow, computeTotalSumByTradeFlow, getProductsForClass, determineReferenceLocation, MetadataMergeType, UIView } from "@atlas-int/data";
import transformProducts from "../transformProducts";
import transformLocations from "../transformLocations";
import {
  DataTransformationResult,
  ProductMetadataBundle,
  TreemapQueryVariables,
} from "../types";
import type { MetadataFetchState } from "@atlas-int/data/types";
import type { LocationMetadataPayload, ProductComplexityPayload } from "@atlas-int/data/types";
import type { RegionMetadata, CountryMetadata } from "@atlas-int/data/types";
import type { TradeDataItem, EnrichedTradeDataItem } from "@atlas-int/data/types";

/** A trade data item with treemap-specific dynamic properties */
interface TreemapTradeDataItem extends EnrichedTradeDataItem {
  isServicesPartner?: boolean;
}

/** Product complexity data item */
interface ProductComplexityItem {
  productId: string;
  pci?: number;
}

// --- Shared helpers ---

/** Merge product data with PCI (Product Complexity Index) values */
const mergeProductDataWithPci = ({
  data,
  complexityData,
}: {
  data: TreemapTradeDataItem[];
  complexityData: ProductComplexityItem[];
}) => {
  return data.map((product: TreemapTradeDataItem) => {
    const matchingComplexityData = complexityData.find(
      (pciProduct: ProductComplexityItem) =>
        pciProduct.productId === product.productId,
    );
    return {
      ...product,
      pci: matchingComplexityData?.pci,
    };
  });
};

/** Split data into services/non-services and compute proportions */
const splitServicesData = ({
  filteredData,
  isServicePredicate,
  tradeFlow,
  tradeDirection,
  tradeDirectionSelector: _tradeDirectionSelector,
}: {
  filteredData: TreemapTradeDataItem[];
  isServicePredicate: (d: TreemapTradeDataItem) => boolean;
  tradeFlow: TradeFlow;
  tradeDirection: TradeDirection;
  tradeDirectionSelector: TradeDirectionSelectorString;
}) => {
  const servicesData = filteredData.filter(isServicePredicate);
  const nonServicesData = filteredData.filter((d) => !isServicePredicate(d));
  const locationHasServices = servicesData.length > 0;

  const totalSum =
    filteredData.length > 0
      ? computeTotalSumByTradeFlow({
          data: filteredData,
          tradeFlow,
          tradeDirection,
        })
      : 0;

  const servicesTotal = locationHasServices
    ? computeTotalSumByTradeFlow({
        data: servicesData,
        tradeFlow,
        tradeDirection,
      })
    : 0;

  const servicesProportion = totalSum > 0 ? servicesTotal / totalSum : 0;

  return {
    servicesData,
    nonServicesData,
    locationHasServices,
    totalSum,
    servicesTotal,
    servicesProportion,
    nonServicesProportion: 1 - servicesProportion,
  };
};

// --- Main hook ---

interface UseTreemapDataTransformationProps {
  inputData: { data: TreemapTradeDataItem[] };
  queryVariables: TreemapQueryVariables;
  hiddenCategories: string[];
  tradeDirection: TradeDirection;
  tradeFlow: TradeFlow;
  tradeDirectionSelector: TradeDirectionSelectorString;
  locationMetadata: MetadataFetchState<LocationMetadataPayload>;
  productMetadata: ProductMetadataBundle;
  productComplexityMetadata: MetadataFetchState<ProductComplexityPayload>;
}

export const useTreemapDataTransformation = ({
  inputData,
  queryVariables,
  hiddenCategories,
  tradeDirection,
  tradeFlow,
  tradeDirectionSelector,
  locationMetadata: locationMetadataState,
  productMetadata,
  productComplexityMetadata: productComplexityState,
}: UseTreemapDataTransformationProps): DataTransformationResult | null => {
  const {
    view,
    productClass,
    productLevel,
    exporter,
    importer,
    year,
    locationLevel,
  } = queryVariables;

  return useMemo((): DataTransformationResult | null => {
    const { data } = inputData as { data: TreemapTradeDataItem[] };

    if (view === UIView.Products) {
      // Get products metadata using shared helper
      const products = getProductsForClass(productClass, {
        hs92: productMetadata.hs92,
        hs12: productMetadata.hs12,
        hs22: productMetadata.hs22,
        sitc: productMetadata.sitc,
      });

      if (!products) return null;

      // Get product complexity for 4-digit products
      const productComplexity: ProductComplexityItem[] | undefined =
        productLevel === ProductLevel.Product4digit &&
        productComplexityState.status === "success"
          ? (productComplexityState.data as unknown as ProductComplexityItem[])
          : undefined;

      const servicesProductId = getProductClassServicesCategoryId({
        productClass,
      });

      const dataWithTopLevelProductId = mergeDataWithTopLevelParent({
        data,
        mergeType: MetadataMergeType.Sector,
        productsMetadata: products,
      });

      // Filter by monetary value and hidden categories
      const filteredData = dataWithTopLevelProductId.filter(
        (d: TreemapTradeDataItem) => {
          const topLevelParent = d.topLevelParent;
          const monetaryValue = computeMonetaryValueByTradeFlow({
            datum: d,
            tradeFlow,
            tradeDirection,
          });
          if (!monetaryValue) return false;
          return !topLevelParent || !hiddenCategories.includes(topLevelParent);
        },
      );

      // Split services/non-services and compute proportions
      const servicesSplit = splitServicesData({
        filteredData,
        isServicePredicate: (d) => d.topLevelParent === servicesProductId,
        tradeFlow,
        tradeDirection,
        tradeDirectionSelector,
      });

      // Merge with PCI if needed
      let dataToTransform = filteredData;
      let servicesDataToTransform = servicesSplit.servicesData;
      let nonServicesDataToTransform = servicesSplit.nonServicesData;

      if (productLevel === ProductLevel.Product4digit && productComplexity) {
        dataToTransform = mergeProductDataWithPci({
          data: filteredData,
          complexityData: productComplexity,
        });
        servicesDataToTransform = mergeProductDataWithPci({
          data: servicesSplit.servicesData,
          complexityData: productComplexity,
        });
        nonServicesDataToTransform = mergeProductDataWithPci({
          data: servicesSplit.nonServicesData,
          complexityData: productComplexity,
        });
      }

      const transformParams = {
        productLevel,
        products,
        totalValue: servicesSplit.totalSum,
        tradeDirection,
        tradeFlow,
      };

      const dataWithRoot = transformProducts({
        ...transformParams,
        data: dataToTransform,
      });
      const servicesDataWithRoot = transformProducts({
        ...transformParams,
        data: servicesDataToTransform,
      });
      const nonServicesDataWithRoot = transformProducts({
        ...transformParams,
        data: nonServicesDataToTransform,
      });

      return {
        dataWithRoot,
        servicesDataWithRoot,
        nonServicesDataWithRoot,
        filteredData,
        servicesData: servicesSplit.servicesData,
        nonServicesData: servicesSplit.nonServicesData,
        totalSum: servicesSplit.totalSum,
        locationHasServices: servicesSplit.locationHasServices,
        servicesProportion: servicesSplit.servicesProportion,
        nonServicesProportion: servicesSplit.nonServicesProportion,
        servicesTotal: servicesSplit.servicesTotal,
        products,
      };
    } else if (view === UIView.Markets) {
      if (locationMetadataState.status !== "success") return null;

      const locationMetadataData = locationMetadataState.data;
      const regions = locationMetadataData.regions as unknown as RegionMetadata[];
      const subregions = locationMetadataData.subregions as unknown as RegionMetadata[];
      const countries = locationMetadataData.countries as unknown as CountryMetadata[];

      const referenceLocation = determineReferenceLocation(exporter, importer);

      // Identify services partners
      const servicesPartnersSet = new Set<string>();
      data.forEach((d: TreemapTradeDataItem) => {
        if (d.partnerCountryId === "country-914") {
          servicesPartnersSet.add(d.partnerCountryId);
        }
      });

      const dataWithServicesPartners = data.map((d: TreemapTradeDataItem) => ({
        ...d,
        isServicesPartner: d.partnerCountryId
          ? servicesPartnersSet.has(String(d.partnerCountryId))
          : false,
      }));

      // Merge with region hierarchy and override services partner topLevelParent
      let dataWithTopLevelParentId = mergeDataWithTopLevelParent({
        data: dataWithServicesPartners,
        mergeType: MetadataMergeType.Region,
        regionsMetadata: regions,
      }).map((d: TreemapTradeDataItem) => ({
        ...d,
        topLevelParent: d.isServicesPartner
          ? servicesPartnersGroupId
          : d.topLevelParent,
      }));

      // Filter by monetary value and hidden categories
      const filteredData = dataWithTopLevelParentId.filter(
        (d: TreemapTradeDataItem) => {
          const monetaryValue = computeMonetaryValueByTradeFlow({
            datum: d,
            tradeFlow,
            tradeDirection,
            tradeDirectionSelector,
          });
          if (!monetaryValue) return false;
          if (hiddenCategories.includes(d.topLevelParent as string))
            return false;
          return true;
        },
      );

      // Split services/non-services and compute proportions
      const servicesSplit = splitServicesData({
        filteredData,
        isServicePredicate: (d) =>
          d.topLevelParent === servicesPartnersGroupId,
        tradeFlow,
        tradeDirection,
        tradeDirectionSelector,
      });

      const isWorldToWorldQuery =
        exporter === worldGroupDatum.groupId &&
        importer === worldGroupDatum.groupId;

      const locationTransformParams = {
        year: year as number,
        location: referenceLocation as string,
        locationDetailLevel: locationLevel,
        regions,
        subregions,
        countries,
        tradeDirection,
        isWorldToWorldQuery,
      };

      const servicesDataWithRoot =
        transformLocations({
          ...locationTransformParams,
          data: servicesSplit.servicesData,
        }) || [];

      const nonServicesDataWithRoot =
        transformLocations({
          ...locationTransformParams,
          data: servicesSplit.nonServicesData,
        }) || [];

      const dataWithRoot =
        transformLocations({
          ...locationTransformParams,
          data: filteredData,
        }) || [];

      return {
        dataWithRoot,
        servicesDataWithRoot,
        nonServicesDataWithRoot,
        filteredData,
        servicesData: servicesSplit.servicesData,
        nonServicesData: servicesSplit.nonServicesData,
        totalSum: servicesSplit.totalSum,
        locationHasServices: servicesSplit.locationHasServices,
        servicesProportion: servicesSplit.servicesProportion,
        nonServicesProportion: servicesSplit.nonServicesProportion,
        servicesTotal: servicesSplit.servicesTotal,
      };
    }

    return null;
  }, [
    inputData,
    view,
    productClass,
    productMetadata,
    productLevel,
    productComplexityState,
    hiddenCategories,
    tradeFlow,
    tradeDirection,
    tradeDirectionSelector,
    locationMetadataState,
    exporter,
    importer,
    year,
    locationLevel,
  ]);
};
